/**
 * The admin's own view of the fabric catalogue -- the same search the buyer
 * uses on `/fabrics` (`Fabrics.tsx`), plus what only the admin may do to it:
 * add a style by hand, edit one, soft-delete/restore one, and mass-upload an
 * `.xlsx` with `docs/fabrics.csv`'s own column layout.
 */
import { useEffect, useRef, useState, type ChangeEvent } from 'react'

import { api } from '../api/client'
import { Button, Empty, Field, Loading, Pill, Problem, measurement } from '../ui'

interface FabricSummary {
  id: string
  label: string
  fabricCode: string
  finishedGsm: number | null
  finishedWidth: string | null
  widthUnit: string | null
  construction: string | null
  deletedAt: string | null
}

interface FabricDetail extends FabricSummary {
  structure: string | null
  fibre: string | null
  yarnCountRaw: string | null
  yarnQuality: string | null
  dyeMethod: string | null
  bedType: string | null
  stitchLengthMm: string | null
  fromFileImport: boolean
}

interface ImportResult {
  rowsRead: number
  rowsUpserted: number
  rowsSkipped: number
}

export function AdminFabrics() {
  const [query, setQuery] = useState('')
  const [showDeleted, setShowDeleted] = useState(false)
  const [results, setResults] = useState<FabricSummary[] | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)
  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [actionId, setActionId] = useState<string | null>(null)
  const [importBusy, setImportBusy] = useState(false)
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const [importError, setImportError] = useState<unknown>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const search = async () => {
    const trimmed = query.trim()
    if (!trimmed) {
      setResults(null)
      return
    }
    setBusy(true)
    setError(null)
    try {
      const result = await api.get<{ fabrics: FabricSummary[] }>(
        `/fabrics?q=${encodeURIComponent(trimmed)}${showDeleted ? '&includeDeleted=true' : ''}`,
      )
      setResults(result.fabrics)
    } catch (problem) {
      setError(problem)
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => void search(), 300)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, showDeleted])

  const onImport = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    setImportBusy(true)
    setImportError(null)
    setImportResult(null)
    try {
      const result = await api.upload<ImportResult>('/fabrics/import', file)
      setImportResult(result)
      if (query.trim()) await search()
    } catch (problem) {
      setImportError(problem)
    } finally {
      setImportBusy(false)
    }
  }

  const onDelete = async (id: string) => {
    setActionId(id)
    setError(null)
    try {
      await api.post(`/fabrics/${id}/delete`)
      await search()
    } catch (problem) {
      setError(problem)
    } finally {
      setActionId(null)
    }
  }

  const onRestore = async (id: string) => {
    setActionId(id)
    setError(null)
    try {
      await api.post(`/fabrics/${id}/restore`)
      await search()
    } catch (problem) {
      setError(problem)
    } finally {
      setActionId(null)
    }
  }

  return (
    <>
      <h1>Fabric catalogue</h1>
      <p className="lede">Search, add, edit, or remove a style. Search by structure, fibre, yarn count or GSM.</p>
      <Problem error={error} />

      <div className="actions">
        <Button
          variant="quiet"
          onClick={() => {
            setAdding((current) => !current)
            setEditingId(null)
          }}
        >
          {adding ? 'Close' : 'Add a fabric'}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx"
          onChange={(event) => void onImport(event)}
          style={{ display: 'none' }}
        />
        <Button variant="quiet" busy={importBusy} onClick={() => fileInputRef.current?.click()}>
          Mass upload (.xlsx)
        </Button>
      </div>

      <Problem error={importError} />
      {importResult ? (
        <p className="field-hint">
          Read {importResult.rowsRead}, upserted {importResult.rowsUpserted}
          {importResult.rowsSkipped > 0 ? `, skipped ${importResult.rowsSkipped} (missing code)` : ''}.
        </p>
      ) : null}

      {adding ? (
        <FabricForm
          onCancel={() => setAdding(false)}
          onDone={async () => {
            setAdding(false)
            await search()
          }}
        />
      ) : null}

      <input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="e.g. single jersey 200 gsm"
        style={{ marginTop: 12 }}
      />
      <label className="picker" style={{ marginTop: 8 }}>
        <input
          type="checkbox"
          checked={showDeleted}
          onChange={(event) => setShowDeleted(event.target.checked)}
        />
        <span>Include deleted</span>
      </label>

      {busy ? <Loading /> : null}
      {!busy && !query.trim() ? <Empty>Type to search the catalogue.</Empty> : null}
      {!busy && query.trim() && results?.length === 0 ? (
        <Empty>No matches. Try fewer or different words.</Empty>
      ) : null}

      {results?.map((fabric) => (
        <div className="card" key={fabric.id}>
          <div className="card-head">
            <strong>{fabric.label}</strong>
            {fabric.deletedAt ? <Pill value="REJECTED" label="Deleted" /> : null}
          </div>
          <div className="reference">{fabric.fabricCode}</div>
          <ul className="specs">
            <li>
              GSM <b>{measurement(fabric.finishedGsm)}</b>
            </li>
            <li>
              Width <b>{measurement(fabric.finishedWidth, fabric.widthUnit)}</b>
            </li>
            {fabric.construction ? (
              <li>
                Construction <b>{fabric.construction}</b>
              </li>
            ) : null}
          </ul>
          <div className="actions">
            <Button
              variant="quiet"
              onClick={() => {
                setEditingId(editingId === fabric.id ? null : fabric.id)
                setAdding(false)
              }}
            >
              {editingId === fabric.id ? 'Close' : 'Edit'}
            </Button>
            {fabric.deletedAt ? (
              <Button busy={actionId === fabric.id} onClick={() => onRestore(fabric.id)}>
                Restore
              </Button>
            ) : (
              <Button
                variant="danger"
                busy={actionId === fabric.id}
                onClick={() => onDelete(fabric.id)}
              >
                Delete
              </Button>
            )}
          </div>
          {editingId === fabric.id ? (
            <EditFabric
              id={fabric.id}
              onCancel={() => setEditingId(null)}
              onDone={async () => {
                setEditingId(null)
                await search()
              }}
            />
          ) : null}
        </div>
      ))}
    </>
  )
}

/** Fetches the full row before showing the form -- the list only carries the search summary's fields. */
function EditFabric({
  id,
  onCancel,
  onDone,
}: {
  id: string
  onCancel: () => void
  onDone: () => void
}) {
  const [fabric, setFabric] = useState<FabricDetail | null>(null)
  const [error, setError] = useState<unknown>(null)

  useEffect(() => {
    void (async () => {
      try {
        const result = await api.get<{ fabric: FabricDetail }>(`/fabrics/${id}`)
        setFabric(result.fabric)
      } catch (problem) {
        setError(problem)
      }
    })()
  }, [id])

  if (!fabric && !error) return <Loading />

  return (
    <>
      <Problem error={error} />
      {fabric ? (
        <>
          {fabric.fromFileImport ? (
            <p className="field-hint" style={{ marginTop: 12 }}>
              This style came from a file import. A later mass upload carrying the same style
              overwrites what's changed here.
            </p>
          ) : null}
          <FabricForm id={fabric.id} initial={fabric} onCancel={onCancel} onDone={onDone} />
        </>
      ) : null}
    </>
  )
}

interface FormState {
  fabricCode: string
  structure: string
  fibre: string
  yarnCountRaw: string
  yarnQuality: string
  dyeMethod: string
  construction: string
  bedType: string
  finishedGsm: string
  finishedWidth: string
  widthUnit: string
  stitchLengthMm: string
}

const BLANK_FORM: FormState = {
  fabricCode: '',
  structure: '',
  fibre: '',
  yarnCountRaw: '',
  yarnQuality: '',
  dyeMethod: '',
  construction: '',
  bedType: '',
  finishedGsm: '',
  finishedWidth: '',
  widthUnit: 'INCH',
  stitchLengthMm: '',
}

function toForm(fabric: FabricDetail): FormState {
  return {
    fabricCode: fabric.fabricCode,
    structure: fabric.structure ?? '',
    fibre: fabric.fibre ?? '',
    yarnCountRaw: fabric.yarnCountRaw ?? '',
    yarnQuality: fabric.yarnQuality ?? '',
    dyeMethod: fabric.dyeMethod ?? '',
    construction: fabric.construction ?? '',
    bedType: fabric.bedType ?? '',
    finishedGsm: fabric.finishedGsm != null ? String(fabric.finishedGsm) : '',
    finishedWidth: fabric.finishedWidth ?? '',
    widthUnit: fabric.widthUnit ?? 'INCH',
    stitchLengthMm: fabric.stitchLengthMm ?? '',
  }
}

/**
 * Create when `id` is absent, edit when it's given. One form for both,
 * matching the rest of this admin screen (`CreateOrganization`, `QuoteForm`
 * in `Admin.tsx`) -- the fields and the request shape are the same either
 * way, only the verb and the endpoint differ.
 */
function FabricForm({
  id,
  initial,
  onDone,
  onCancel,
}: {
  id?: string
  initial?: FabricDetail
  onDone: () => void
  onCancel: () => void
}) {
  const [form, setForm] = useState<FormState>(initial ? toForm(initial) : BLANK_FORM)
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  const set = (key: keyof FormState) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [key]: event.target.value }))

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const body = {
        fabricCode: form.fabricCode.trim(),
        structure: form.structure.trim() || null,
        fibre: form.fibre.trim() || null,
        yarnCountRaw: form.yarnCountRaw.trim() || null,
        yarnQuality: form.yarnQuality.trim() || null,
        dyeMethod: form.dyeMethod.trim() || null,
        construction: form.construction.trim() || null,
        bedType: form.bedType.trim() || null,
        finishedGsm: form.finishedGsm.trim() ? form.finishedGsm.trim() : null,
        finishedWidth: form.finishedWidth.trim() ? form.finishedWidth.trim() : null,
        widthUnit: form.finishedWidth.trim() ? form.widthUnit : null,
        stitchLengthMm: form.stitchLengthMm.trim() ? form.stitchLengthMm.trim() : null,
      }
      if (id) {
        await api.patch(`/fabrics/${id}`, body)
      } else {
        await api.post('/fabrics', body)
      }
      onDone()
    } catch (problem) {
      setError(problem)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} style={{ marginTop: 12 }}>
      <Problem error={error} />
      <Field label="Fabric code">
        <input required value={form.fabricCode} onChange={set('fabricCode')} placeholder="JP0002C201T" />
      </Field>
      <div className="row">
        <Field label="Structure" hint="e.g. SINGLE_JERSEY">
          <input value={form.structure} onChange={set('structure')} />
        </Field>
        <Field label="Fibre" hint="e.g. COTTON">
          <input value={form.fibre} onChange={set('fibre')} />
        </Field>
      </div>
      <div className="row">
        <Field label="Yarn count" hint="e.g. Ne 20/1">
          <input value={form.yarnCountRaw} onChange={set('yarnCountRaw')} />
        </Field>
        <Field label="Yarn quality" hint="e.g. COMBED">
          <input value={form.yarnQuality} onChange={set('yarnQuality')} />
        </Field>
      </div>
      <div className="row">
        <Field label="Dye method" hint="e.g. PIECE_DYED">
          <input value={form.dyeMethod} onChange={set('dyeMethod')} />
        </Field>
        <Field label="Construction" hint="e.g. TUBULAR">
          <input value={form.construction} onChange={set('construction')} />
        </Field>
      </div>
      <Field label="Bed type" hint="e.g. SINGLE_JERSEY">
        <input value={form.bedType} onChange={set('bedType')} />
      </Field>
      <Field label="GSM">
        <input inputMode="numeric" value={form.finishedGsm} onChange={set('finishedGsm')} />
      </Field>
      <div className="row">
        <Field label="Width">
          <input inputMode="decimal" value={form.finishedWidth} onChange={set('finishedWidth')} />
        </Field>
        <Field label="Unit" hint="Tubular in inches, open width in cm">
          <select value={form.widthUnit} onChange={set('widthUnit')}>
            <option value="INCH">inches</option>
            <option value="CM">cm</option>
          </select>
        </Field>
      </div>
      <Field label="Stitch length (mm)">
        <input inputMode="decimal" value={form.stitchLengthMm} onChange={set('stitchLengthMm')} />
      </Field>
      <div className="actions">
        <Button type="submit" busy={busy}>
          {id ? 'Save' : 'Add fabric'}
        </Button>
        <Button type="button" variant="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  )
}
